import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { useWorkflowReferenceResolve } from "@/hooks/api/queries/workflows.queries";
import type {
  WorkflowReferenceAction,
  WorkflowReferenceItem,
  WorkflowReferenceKind,
  WorkflowReferenceResolveRef,
} from "@/types/workflow-reference.types";

type RegisterReferenceSource = (key: string, ref: WorkflowReferenceResolveRef | null) => void;

interface ReferenceResolutionContextValue {
  itemsByKey: Record<string, WorkflowReferenceItem>;
  isLoading: boolean;
  registerReferenceSource: RegisterReferenceSource;
}

const ReferenceResolutionContext = createContext<ReferenceResolutionContextValue | null>(null);

export function workflowReferenceKey(
  kind: WorkflowReferenceKind,
  id: string,
  action: WorkflowReferenceAction = "read",
) {
  return `${kind}:${action}:${id}`;
}

function groupRefs(refs: WorkflowReferenceResolveRef[]) {
  const groups = new Map<string, WorkflowReferenceResolveRef>();
  for (const ref of refs) {
    const ids = [...new Set(ref.ids.filter(Boolean))];
    if (ids.length === 0) continue;
    const key = [ref.kind, ref.action ?? "read", ref.nodeType ?? "", ref.fieldKey ?? ""].join(":");
    const existing = groups.get(key);
    if (existing) {
      existing.ids = [...new Set([...existing.ids, ...ids])].sort();
    } else {
      groups.set(key, { ...ref, action: ref.action ?? "read", ids: ids.sort() });
    }
  }
  return [...groups.values()].sort((a, b) =>
    [a.kind, a.action ?? "read", a.nodeType ?? "", a.fieldKey ?? ""].join(":").localeCompare(
      [b.kind, b.action ?? "read", b.nodeType ?? "", b.fieldKey ?? ""].join(":"),
    ),
  );
}

function indexResolvedItems(
  refs: WorkflowReferenceResolveRef[],
  rows: WorkflowReferenceItem[],
) {
  const byNaturalKey = new Map(rows.map((item) => [`${item.kind}:${item.id}`, item]));
  const entries: Array<[string, WorkflowReferenceItem]> = [];
  for (const ref of refs) {
    for (const id of ref.ids) {
      const item = byNaturalKey.get(`${ref.kind}:${id}`);
      if (!item) continue;
      entries.push([workflowReferenceKey(ref.kind, id, ref.action ?? "read"), item]);
    }
  }
  return Object.fromEntries(entries);
}

export function ReferenceResolutionProvider({
  children,
  enabled = true,
}: {
  children: React.ReactNode;
  enabled?: boolean;
}) {
  const [sources, setSources] = useState<Record<string, WorkflowReferenceResolveRef>>({});

  const registerReferenceSource = useCallback<RegisterReferenceSource>((key, ref) => {
    setSources((prev) => {
      if (!ref || ref.ids.length === 0) {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: ref };
    });
  }, []);

  const refs = useMemo(() => groupRefs(Object.values(sources)), [sources]);
  const query = useWorkflowReferenceResolve(refs, { enabled: enabled && refs.length > 0 });

  const itemsByKey = useMemo(() => {
    const rows = [...(query.data?.data.items ?? []), ...(query.data?.data.missing ?? [])];
    return indexResolvedItems(refs, rows);
  }, [query.data, refs]);
  const contextValue = useMemo(
    () => ({
      itemsByKey,
      isLoading: query.isLoading || query.isFetching,
      registerReferenceSource,
    }),
    [itemsByKey, query.isFetching, query.isLoading, registerReferenceSource],
  );

  return (
    <ReferenceResolutionContext.Provider value={contextValue}>
      {children}
    </ReferenceResolutionContext.Provider>
  );
}

export function useResolvedWorkflowReferences({
  kind,
  action = "read",
  nodeType,
  fieldKey,
  ids,
}: {
  kind: WorkflowReferenceKind;
  action?: WorkflowReferenceAction;
  nodeType?: string;
  fieldKey?: string;
  ids: string[];
}) {
  const ctx = useContext(ReferenceResolutionContext);
  const sourceKey = useId();
  const idsKey = useMemo(() => [...new Set(ids.filter(Boolean))].sort().join("|"), [ids]);
  const cleanIDs = useMemo(() => (idsKey ? idsKey.split("|") : []), [idsKey]);
  const registerReferenceSource = ctx?.registerReferenceSource;

  useEffect(() => {
    if (!registerReferenceSource) return;
    registerReferenceSource(sourceKey, {
      kind,
      action,
      nodeType,
      fieldKey,
      ids: cleanIDs,
    });
    return () => registerReferenceSource(sourceKey, null);
  }, [action, cleanIDs, fieldKey, idsKey, kind, nodeType, registerReferenceSource, sourceKey]);

  const directRefs = useMemo(
    () => [{ kind, action, nodeType, fieldKey, ids: cleanIDs }],
    [action, cleanIDs, fieldKey, kind, nodeType],
  );
  const directQuery = useWorkflowReferenceResolve(directRefs, {
    enabled: !ctx && cleanIDs.length > 0,
  });

  const directItemsByKey = useMemo(() => {
    const rows = [
      ...(directQuery.data?.data.items ?? []),
      ...(directQuery.data?.data.missing ?? []),
    ];
    return indexResolvedItems(directRefs, rows);
  }, [directQuery.data, directRefs]);

  return {
    itemsByKey: ctx?.itemsByKey ?? directItemsByKey,
    isLoading: ctx?.isLoading ?? directQuery.isLoading,
  };
}
